import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Modal, Input, Collapse } from "antd";
import Sidebar from "./Sidebar";
import { FaBarsStaggered } from "react-icons/fa6";
import axios from "axios";
import AdminNav from "./AdminNav";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import { FiEdit, FiChevronDown, FiChevronUp } from "react-icons/fi";

const { Panel } = Collapse;

const FundRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [activePanels, setActivePanels] = useState(["1", "2", "3", "4"]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const token = localStorage.getItem("admintoken");

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API}/api/v1/fund/get-fund-req/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (data?.success) {
          setSubscription(data.subscription);
        } else {
          toast.error(data.message);
          navigate("/dashboard/admin/fund-requests");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch subscription details");
        navigate("/dashboard/admin/fund-requests");
      } finally {
        setLoading(false);
      }
    };
    fetchSubscriptionData();
  }, [id, token, navigate]);

  const handleApprove = async () => {
    setLoading(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API}/api/v1/fund/app-fund-req/${id}`,
        {}, // empty body since you're not sending any data
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.success) {
        toast.success(
          res.data.message || "Fund subscription approved successfully"
        );
        navigate("/dashboard/admin/fund-requests");
      } else {
        toast.error(res.data.message || "Approval failed");
      }
    } catch (error) {
      console.error("Approval error:", error);
      if (error.response) {
        // Server responded with a status code outside 2xx
        toast.error(
          error.response.data.message || "Failed to approve fund subscription"
        );
      } else if (error.request) {
        // Request was made but no response received
        toast.error("No response from server. Please check your connection.");
      } else {
        // Something happened in setting up the request
        toast.error("Request setup error: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReject = () => setRejectModalVisible(true);

  const submitRejection = async () => {
    setLoading(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API}/api/v1/fund/rej-fund-req/${id}`,
        { rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success("Fund subscription rejected successfully");
        setRejectModalVisible(false);
        navigate("/dashboard/admin/fund-requests");
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to reject fund subscription");
    } finally {
      setLoading(false);
      setRejectModalVisible(false);
      setRejectReason("");
    }
  };

  const formatDate = (date) => {
    return date ? new Date(date).toLocaleDateString() : "N/A";
  };

  const renderField = (label, value, isLink = false) => (
    <label className="d-flex justify-content-between mb-2">
      <span>{label}:</span>
      <span>
        {isLink ? (
          <Link target="_blank" to={value}>
            <FiEdit />
          </Link>
        ) : (
          value || "N/A"
        )}
      </span>
    </label>
  );

  if (!subscription) return <Loader />;

  return (
    <div className="wrapper d-flex align-items-stretch">
      {loading && <Loader />}
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="content" className="px-2 pt-3">
        <button type="button" onClick={toggleSidebar} className="bars-btn">
          <FaBarsStaggered />
        </button>
        <AdminNav />

        <div className="shadow-lg rounded-3 p-4 deposit-card">
          <h4 className="mt-3 text-uppercase">
            Fund Subscription Request - {subscription.status}
          </h4>

          <Collapse
            activeKey={activePanels}
            onChange={(keys) => setActivePanels(keys)}
            expandIconPosition="end"
            expandIcon={({ isActive }) =>
              isActive ? <FiChevronUp /> : <FiChevronDown />
            }
          >
            {/* Basic Information */}
            <Panel header="Basic Information" key="1">
              <div className="border rounded-2 px-3 py-2">
                {renderField("Fund", subscription.fundId?.name)}
                {renderField("Class", subscription.classId?.class)}
                {renderField(
                  "Subscription Amount",
                  `${subscription.subscriptionSum} ${subscription.subscriptionCurrency}`
                )}
                {renderField(
                  "Amount in Words",
                  subscription.subscriptionInWords
                )}
                {renderField("Beneficiary", subscription.beneficiary)}
                {renderField("First Name", subscription.firstName)}
                {renderField("Surname", subscription.surname)}
                {renderField("Email", subscription.email)}
                {renderField("Phone", subscription.phone)}
              </div>
            </Panel>

            {/* Personal Details */}
            <Panel header="Personal Details" key="2">
              <div className="border rounded-2 px-3 py-2">
                {renderField("Date of Birth", formatDate(subscription.dob))}
                {renderField("Place of Birth", subscription.placeOfBirth)}
                {renderField("Nationality", subscription.nationality)}
                {renderField("Passport ID", subscription.passportID)}
                {renderField("Date of Issue", formatDate(subscription.doi))}
                {renderField("Date of Expiry", formatDate(subscription.doe))}
                {renderField("Occupation", subscription.occupation)}
                {renderField("Industry", subscription.industry)}
                {renderField("Holds Public Office", subscription.publicOffice)}
                {subscription.publicOffice === "Yes" &&
                  renderField("Office Details", subscription.officeDetails)}
              </div>
            </Panel>

            {/* Address Information */}
            <Panel header="Address Information" key="3">
              <div className="border rounded-2 px-3 py-2">
                {renderField("Address", subscription.address)}
                {renderField("City", subscription.city)}
                {renderField("State/Province", subscription.state)}
                {renderField("Postal Code", subscription.postal)}
                {renderField("Country", subscription.country)}
                {renderField("Mailing Address", subscription.mailingAddress)}
              </div>
            </Panel>

            {/* Financial Information */}
            <Panel header="Financial Information" key="4">
              <div className="border rounded-2 px-3 py-2">
                {renderField("Source of Capital", subscription.sourceOfCapital)}
                {renderField(
                  "Source Details",
                  subscription.sourceOfCapitalDetails
                )}
                {renderField("Bank Name", subscription.bankName)}
                {renderField("BIC/SWIFT Code", subscription.bicCode)}
                {renderField("Bank Address", subscription.bankAddress)}
                {renderField("IBAN", subscription.iban)}
                {renderField("Bank Country", subscription.bankCountry)}
                {renderField(
                  "Transaction Authority",
                  subscription.transactionAuthority
                )}
                {renderField(
                  "Business Connection",
                  subscription.businessConnection
                )}
              </div>
            </Panel>

            {/* Arranger & Documents */}
            <Panel header="Arranger & Documents" key="5">
              <div className="border rounded-2 px-3 py-2">
                {renderField("Arranger Name", subscription.arrangerName)}
                {renderField("Arranger Email", subscription.arrangerEmail)}
                {renderField("Arranger Company", subscription.arrangerCompany)}
                {renderField("Arranger Phone", subscription.arrangerPhone)}
              </div>
            </Panel>
            <Panel header="Signature" key="5">
              <div className="border rounded-2 px-3 py-2">
                {renderField("Signature", subscription.signature, true)}
              </div>
            </Panel>
          </Collapse>

          {subscription.status === "Pending" && (
            <div className="d-flex justify-content-end mt-5">
              <button onClick={handleApprove} className="btn btn-success me-3">
                APPROVE
              </button>
              <button onClick={handleReject} className="btn btn-danger">
                REJECT
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      <Modal
        title="Reject Fund Subscription"
        open={isRejectModalVisible}
        onOk={submitRejection}
        onCancel={() => setRejectModalVisible(false)}
        okText="Submit Rejection"
        cancelText="Cancel"
        confirmLoading={loading}
      >
        <Input.TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Please specify the reason for rejection"
          required
        />
      </Modal>
    </div>
  );
};

export default FundRequestDetails;
